import React, { useState } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, RotateCcw, Edit3, Trash2 } from 'lucide-react';
import StatusBadge from './StatusBadge';

const Field = ({ label, children }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>{label}</dt>
    <dd className="text-sm mt-0.5" style={{ color: '#1f2937' }}>{children}</dd>
  </div>
);

const ACCENT_BY_STATUS = {
  pending: '#f59e0b',
  live: '#16a34a',
  rejected: '#DC143C',
  race: '#9ca3af',
};

// Card layout — same visual DNA as factory-rate-intelligence-center's FactoryCard (rounded-2xl, soft
// shadow, status-colored top accent, click-to-expand collapsible detail section). Used for BOTH mobile
// and desktop now via a responsive CSS grid in ApprovalGrid.jsx, replacing the old isMobile JS branch
// (a wide <table> for desktop, a separate full-inline card for mobile) with one shared component —
// pure markup restructuring, every handler below is called with the exact same arguments as before.
const GridRow = ({
  row,
  role,
  isSelected,
  onSelect,
  onApprove,
  onReject,
  onEdit,
  onDelete,
  onRowClick,
  tab,
}) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValues, setEditValues] = useState({ rateExcl: row?.rateExcl, qty: row?.qty });
  const [detailsOpen, setDetailsOpen] = useState(false);

  const isPending = tab === 'pending';
  const byLabel = isPending ? 'Submitted By' : 'Approved By';
  const atLabel = isPending ? 'Submitted At' : 'Approved At';

  // Approve/Reject (and inline rate/qty editing) only make sense for rows still awaiting a decision —
  // scope them to the Pending Approval tab so they don't render on Live Rates rows.
  const canAct = (role === 'Ops Manager' || role === 'Admin') && row?.status !== 'race' && tab === 'pending';
  const canDeleteLive = (role === 'Ops Manager' || role === 'Admin') && tab === 'live' && row?.status === 'live';
  const isRace = row?.status === 'race';
  const isGreyed = isRace;

  const handleCellEdit = (field, value) => {
    const updated = { ...editValues, [field]: value };
    setEditValues(updated);
    onEdit(row?.id, field, value);
  };

  const handleRejectConfirm = () => {
    if (rejectReason?.trim()) {
      onReject(row?.id, rejectReason);
      setRejectOpen(false);
      setRejectReason('');
    }
  };

  const rateIncl = (editValues?.rateExcl * 1.05)?.toFixed(0);
  const hasEdits = editValues?.rateExcl !== row?._originalRateExcl || editValues?.qty !== row?._originalQty;
  const accent = ACCENT_BY_STATUS?.[row?.status] || '#f59e0b';

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden"
      style={{
        border: '1px solid #e8ddd0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderTop: `3px solid ${accent}`,
        opacity: isGreyed ? 0.6 : 1,
        backgroundColor: isSelected ? '#fffbeb' : '#fff',
      }}
    >
      <div className="p-4">
        {/* ── Always-visible: identity + status ── */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {canAct && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(row?.id)}
                className="rounded mt-1 flex-shrink-0"
                style={{ accentColor: '#f59e0b' }}
              />
            )}
            <h3 className="font-bold text-sm leading-tight" style={{ color: '#1f2937' }}>{row?.millName}</h3>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <StatusBadge status={row?.status} small />
            {hasEdits && <StatusBadge status="edited" small />}
          </div>
        </div>

        {/* Grade + Season pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
            {row?.grade}
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
            {row?.season}
          </span>
        </div>

        {/* Rate + Qty — the two most important numbers, prominent */}
        <div className="grid grid-cols-3 gap-2 rounded-xl p-3 mb-3" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div onClick={e => e?.stopPropagation()}>
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#92400e' }}>Rate Excl GST</p>
            <div className="flex items-center gap-1 mt-0.5">
              {canAct && editingCell === 'rateExcl' ? (
                <input
                  autoFocus
                  type="number"
                  value={editValues?.rateExcl}
                  onChange={e => handleCellEdit('rateExcl', parseInt(e?.target?.value) || 0)}
                  onBlur={() => setEditingCell(null)}
                  onKeyDown={e => e?.key === 'Enter' && setEditingCell(null)}
                  className="border rounded px-1.5 py-0.5 text-sm outline-none w-full"
                  style={{ borderColor: '#f59e0b' }}
                />
              ) : (
                <span
                  className={`text-sm font-bold ${canAct ? 'cursor-pointer hover:underline' : ''}`}
                  style={{ color: '#1f2937' }}
                  onClick={() => canAct && setEditingCell('rateExcl')}
                >
                  ₹{editValues?.rateExcl?.toLocaleString('en-IN')}
                </span>
              )}
              {canAct && editingCell !== 'rateExcl' && (
                <Edit3 size={10} style={{ color: '#9ca3af', cursor: 'pointer' }} onClick={() => setEditingCell('rateExcl')} />
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#92400e' }}>Rate Incl GST</p>
            <p className="text-sm font-bold mt-0.5" style={{ color: '#1f2937' }}>₹{parseInt(rateIncl)?.toLocaleString('en-IN')}</p>
          </div>
          <div onClick={e => e?.stopPropagation()}>
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#92400e' }}>Qty (MT)</p>
            <div className="flex items-center gap-1 mt-0.5">
              {canAct && editingCell === 'qty' ? (
                <input
                  autoFocus
                  type="number"
                  value={editValues?.qty}
                  onChange={e => handleCellEdit('qty', parseInt(e?.target?.value) || 0)}
                  onBlur={() => setEditingCell(null)}
                  onKeyDown={e => e?.key === 'Enter' && setEditingCell(null)}
                  className="border rounded px-1.5 py-0.5 text-sm outline-none w-full"
                  style={{ borderColor: '#f59e0b' }}
                />
              ) : (
                <span
                  className={`text-sm font-bold ${canAct ? 'cursor-pointer hover:underline' : ''}`}
                  style={{ color: '#1f2937' }}
                  onClick={() => canAct && setEditingCell('qty')}
                >
                  {editValues?.qty}
                </span>
              )}
              {canAct && editingCell !== 'qty' && (
                <Edit3 size={10} style={{ color: '#9ca3af', cursor: 'pointer' }} onClick={() => setEditingCell('qty')} />
              )}
            </div>
          </div>
        </div>

        {/* ── Collapsible "..." section — closed by default ── */}
        <button
          onClick={() => setDetailsOpen(o => !o)}
          className="flex items-center gap-1 text-xs font-semibold mb-2"
          style={{ color: '#d97706' }}
        >
          {detailsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {detailsOpen ? 'Hide details' : 'More details'}
        </button>

        {detailsOpen && (
          <div className="rounded-xl p-3 mb-3" style={{ background: '#f8f1e9', border: '1px solid #e8ddd0' }}>
            <dl className="grid grid-cols-2 gap-3">
              <Field label="Payment Date">{row?.paymentDate}</Field>
              <Field label="Lifting Date">{row?.liftingDate}</Field>
              <Field label={byLabel}>{row?.submittedBy || row?.approvedBy || '—'}</Field>
              <Field label={atLabel}>{row?.submittedAt || row?.approvedAt || '—'}</Field>
            </dl>
            <button
              onClick={() => onRowClick(row)}
              className="mt-3 text-xs font-semibold underline"
              style={{ color: '#d97706' }}
            >
              View audit trail
            </button>
          </div>
        )}

        {/* ── Actions — always visible, never collapsed ── */}
        {canAct && !rejectOpen && (
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(row?.id)}
              className="flex-1 py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 transition hover:opacity-90"
              style={{ backgroundColor: '#16a34a' }}
            >
              <CheckCircle size={13} /> Approve
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              className="flex-1 py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 transition hover:opacity-90"
              style={{ backgroundColor: '#DC143C' }}
            >
              <XCircle size={13} /> Reject
            </button>
          </div>
        )}
        {canAct && rejectOpen && (
          <div className="flex flex-col gap-2">
            <input
              autoFocus
              value={rejectReason}
              onChange={e => setRejectReason(e?.target?.value)}
              placeholder="Enter rejection reason..."
              className="border rounded-lg px-3 py-2 text-xs outline-none w-full"
              style={{ borderColor: '#e8ddd0' }}
              onKeyDown={e => e?.key === 'Enter' && handleRejectConfirm()}
            />
            <div className="flex gap-2">
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectReason?.trim()}
                className="flex-1 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: '#DC143C' }}
              >
                Confirm Reject
              </button>
              <button
                onClick={() => { setRejectOpen(false); setRejectReason(''); }}
                className="flex-1 py-2 rounded-lg text-xs font-semibold border"
                style={{ borderColor: '#e8ddd0', color: '#4b5563' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {(row?.approvedBy || canDeleteLive) && (
          <div className="flex gap-2 mt-2">
            {row?.approvedBy && (
              <button
                onClick={() => onApprove && onApprove(row, 'revert')}
                title={row?.hasPriorSnapshot ? undefined : 'No prior snapshot'}
                disabled={!row?.hasPriorSnapshot}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: '#e8ddd0', color: '#d97706' }}
              >
                <RotateCcw size={12} /> Revert
              </button>
            )}
            {canDeleteLive && (
              <button
                onClick={() => onDelete && onDelete(row)}
                title="Delete this offer — the mill itself is not affected"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition hover:bg-red-50"
                style={{ borderColor: '#fecaca', color: '#DC143C' }}
              >
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GridRow;
